<template>
  <article
    class="kds-persona-card"
    :class="[
      { 'kds-persona-card--speaking': speaking },
      variant === 'detail' ? 'kds-persona-card--detail' : '',
      variant === 'list' ? 'kds-persona-card--list' : '',
      glass === 'glass' ? 'kds-persona-card--glass' : ''
    ]"
  >
    <header class="kds-persona-card__header">
      <KdsAvatar :segment="segment" :size="48" :speaking="speaking" />
      <div class="kds-persona-card__id">
        <h3 class="kds-persona-card__name">{{ name }}</h3>
        <span class="kds-persona-card__code">{{ personaId }}</span>
      </div>
      <KdsBadge v-if="speaking" tone="live" class="kds-persona-card__live">
        Speaking
      </KdsBadge>
    </header>
    <KdsSegmentChip
      class="kds-persona-card__segment"
      :segment="segment"
      :code="segmentCode"
      :name="segmentLabel"
    />
    <KdsBadge
      v-if="defaultBadge"
      tone="muted"
      class="kds-persona-card__default-badge"
    >
      {{ defaultBadgeLabel }}
    </KdsBadge>
    <p v-if="bio" class="kds-persona-card__bio">{{ bio }}</p>
    <!-- Optional CTA slot — e.g. an "Open persona" button in the canvas popover -->
    <slot name="cta" />
  </article>
</template>

<script setup lang="ts">
import KdsAvatar from '@/kds/primitives/KdsAvatar.vue';
import KdsBadge from '@/kds/primitives/KdsBadge.vue';
import KdsSegmentChip from './KdsSegmentChip.vue';
import type { PersonaSegment, KdsPersonaCardVariant } from '@/kds/types';

interface Props {
  name: string;
  personaId: string;
  segment: PersonaSegment;
  segmentCode: string;
  segmentLabel: string;
  bio?: string;
  speaking?: boolean;
  defaultBadge?: boolean;
  defaultBadgeLabel?: string;
  variant?: KdsPersonaCardVariant;
  /** Surface variant: 'glass' applies the glassmorphism mixin (D1b — dashboard personas rail). */
  glass?: 'default' | 'glass';
}

withDefaults(defineProps<Props>(), {
  bio: undefined,
  speaking: false,
  defaultBadge: false,
  defaultBadgeLabel: 'Default',
  variant: 'compact',
  glass: 'default'
});
</script>

<style scoped lang="scss">
@use '@/css/kds-glass' as glass;

.kds-persona-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
  background: var(--paper);
  border: var(--border);
  border-radius: var(--r-4);
  transition:
    background-color var(--d-base) var(--ease-out),
    border-color var(--d-base) var(--ease-out);

  // D1(b): glass surface for dashboard persona rail cards.
  // D1(a): softer hover shadow on glass surface.
  &--glass {
    @include glass.kds-glass-surface;

    &:hover {
      box-shadow: var(--shadow-1);
    }
  }

  &--speaking {
    background: var(--signal-tint);
    border-color: var(--signal-press);
  }

  &--detail {
    padding: var(--space-7);
  }

  // List variant: horizontal compact layout for popovers and rails.
  // Avatar + name/id inline, no bio clamp, smaller padding.
  &--list {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--r-3);

    .kds-persona-card__header {
      // In list mode the header takes all horizontal space.
      flex: 1;
      min-width: 0;
    }

    .kds-persona-card__segment {
      display: none;
    }

    .kds-persona-card__bio {
      display: none;
    }
  }

  &__header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: var(--space-3);
  }

  &__id {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  &__name {
    margin: 0;
    font-size: var(--fs-h3);
    line-height: var(--lh-h3);
    color: var(--ink);
  }

  &__code {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
  }

  &__default-badge {
    align-self: flex-start;
    margin-top: var(--space-1);
  }

  &__bio {
    margin: 0;
    color: var(--stone);
    font-size: var(--fs-body-sm);
    line-height: var(--lh-body-sm);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}
</style>
